import type { components } from '@feedfm/clips-api-client';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Alert,
  Box,
  Button,
  Collapse,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDemoAppContext } from '../../pages/demo/DemoAppContext';
import Player from '../Player/Player';
import './MusicFetch.css';

const MusicFetch = () => {
  const [clipId, setClipId] = useState('');
  const [response, setResponse] = useState<
    components['schemas']['MusicFetchResponse'] | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const [isResponseExpanded, setIsResponseExpanded] = useState(false);
  const [playResponse, setPlayResponse] = useState<{
    success: boolean;
    message?: string;
  } | null>(null);
  const [playCalledForClipId, setPlayCalledForClipId] = useState<string | null>(
    null,
  );
  const { client, clientId } = useDemoAppContext();

  // Reset playCalledForClipId when a new response is fetched
  useEffect(() => {
    setPlayCalledForClipId(null);
    setPlayResponse(null);
  }, [response]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setResponse(null);

    try {
      const response = await client.POST('/music-fetch', {
        body: { clientId, clipIds: [Number(clipId)] },
      });

      if (response.data) {
        setResponse(response.data);
      } else if (response.error) {
        throw new Error(response.error);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handlePlay = async () => {
    // Only call play endpoint if not already called for this clipId
    if (playCalledForClipId === clipId) return;

    try {
      const playResponse = await client.POST('/play', {
        body: {
          clientId,
          clipId: Number(clipId),
          method: 'Stream',
          preview: true,
          offline: false,
        },
      });

      if (playResponse.error) {
        throw new Error(`HTTP error! status: ${playResponse.error ?? 'bla'}`);
      }

      setPlayResponse(null);
      setPlayCalledForClipId(clipId);
    } catch (err) {
      setPlayResponse({
        success: false,
        message: err instanceof Error ? err.message : 'An error occurred',
      });
      setPlayCalledForClipId(clipId);
    }
  };

  return (
    <Paper elevation={3} className="music-fetch-paper">
      <Typography variant="h5" component="h2" gutterBottom>
        Music Fetch (POST /music-fetch)
      </Typography>

      <form onSubmit={handleSubmit}>
        <Box className="music-fetch-container">
          <TextField
            fullWidth
            label="Clip ID"
            variant="outlined"
            value={clipId}
            onChange={(e) => {
              setClipId(e.target.value);
            }}
            className="music-fetch-textfield"
            type="number"
            required
          />

          <Button
            fullWidth
            variant="contained"
            type="submit"
            disabled={client.getToken() === ''}
            className="music-fetch-button"
          >
            Fetch Music
          </Button>
        </Box>
      </form>

      {error && (
        <Alert severity="error" className="music-fetch-error">
          {error}
        </Alert>
      )}

      {response && (
        <Box className="music-fetch-response">
          <Box className="music-fetch-response-header">
            <Typography variant="h6" gutterBottom>
              Response:
            </Typography>
            <IconButton
              onClick={() => {
                setIsResponseExpanded(!isResponseExpanded);
              }}
            >
              {isResponseExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Collapse in={isResponseExpanded}>
            <Paper variant="outlined" className="music-fetch-response-paper">
              <pre className="music-fetch-response-pre">
                {JSON.stringify(response, null, 2)}
              </pre>
            </Paper>
          </Collapse>
          <Box className="music-fetch-player">
            {response &&
              'success' in response &&
              response.success &&
              'data' in response &&
              response.data &&
              Array.isArray(response.data) &&
              response.data.length > 0 &&
              'success' in response.data[0] &&
              response.data[0].success &&
              'data' in response.data[0] &&
              response.data[0].data && (
                <Player
                  preSignedUrl={response.data[0].data.preSignedUrl || null}
                  artworkPreSignedUrl={
                    response.data[0].data.artworkPreSignedUrl
                  }
                  title={response.data[0].data.title}
                  artist={response.data[0].data.artist}
                  release={response.data[0].data.release}
                  onPlay={handlePlay}
                />
              )}
            {playResponse && (
              <Box sx={{ mt: 2 }}>
                <Paper
                  elevation={1}
                  sx={{
                    mt: 2,
                    p: 2,
                    backgroundColor: 'primary.light',
                    color: 'primary.contrastText',
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    /play endpoint called for Clip ID: {clipId}
                  </Typography>
                </Paper>
                <Paper
                  variant="outlined"
                  className="music-fetch-response-paper music-fetch-play-response-paper"
                >
                  <pre className="music-fetch-response-pre">
                    {JSON.stringify(playResponse, null, 2)}
                  </pre>
                </Paper>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default MusicFetch;
